import { motion, useReducedMotion } from 'framer-motion'
import { Briefcase, DollarSign, Layers, UserCheck } from 'lucide-react'
import { TimeGoalHeroCard } from '@/components/Dashboard/TimeGoalHeroCard'
import {
  OPERACAO_METRIC_ACCENTS,
  OperacaoMetricCard,
} from '@/components/Dashboard/OperacaoMetricCard'
import { getDashboardVariants } from '@/components/Dashboard/dashboardMotion'
import type { TimeTabView } from '@/components/Dashboard/timeTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'

type Props = {
  view: TimeTabView
  valuesVisible?: boolean
}

export function TimeKpiGrid({ view, valuesVisible = false }: Props) {
  const reduced = useReducedMotion()
  const v = getDashboardVariants(reduced)

  const secondaryCards = [
    {
      key: 'closers',
      label: 'Total closers',
      value: formatDashboardNumber(view.totalClosers, valuesVisible),
      numericValue: view.totalClosers,
      valueFormat: 'integer' as const,
      subtitle: `${formatDashboardNumber(view.teamMembers.length, valuesVisible)} com movimento no período`,
      trendPositive: view.totalClosers > 0,
      icon: <UserCheck size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.winRate,
    },
    {
      key: 'closed-deals',
      label: 'Deals fechados',
      value: formatDashboardNumber(view.closedDeals, valuesVisible),
      numericValue: view.closedDeals,
      valueFormat: 'integer' as const,
      subtitle: formatDashboardCurrency(view.closedRevenue, valuesVisible),
      trendPositive: view.closedDeals > 0,
      icon: <Briefcase size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.closed,
    },
    {
      key: 'pipeline-value',
      label: 'Pipeline ativo',
      value: formatDashboardCurrency(view.pipelineTotalValue, valuesVisible),
      numericValue: view.pipelineTotalValue,
      valueFormat: 'currency' as const,
      subtitle: `${formatDashboardNumber(view.pipelineTotalCount, valuesVisible)} negociações abertas`,
      trendPositive: view.pipelineTotalCount > 0,
      icon: <DollarSign size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.pipeline,
    },
    {
      key: 'pipeline-closers',
      label: 'Closers com pipeline',
      value: formatDashboardNumber(view.pipelineByCloser.length, valuesVisible),
      numericValue: view.pipelineByCloser.length,
      valueFormat: 'integer' as const,
      subtitle:
        view.pipelineByCloser.length > 0
          ? 'Com deals fora de ganho/perdido'
          : 'Nenhum deal aberto no funil',
      trendPositive: view.pipelineByCloser.length > 0,
      icon: <Layers size={16} />,
      accent: { bg: '#ede9fe', text: '#7c3aed', ring: '#c4b5fd' },
    },
  ]

  return (
    <motion.div
      variants={v.kpiGrid}
      initial='hidden'
      animate='show'
      className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 xl:grid-rows-2'
    >
      <motion.div variants={v.kpiItem} className='sm:col-span-2 xl:row-span-2'>
        <TimeGoalHeroCard view={view} valuesVisible={valuesVisible} />
      </motion.div>

      {secondaryCards.map((kpi, index) => (
        <motion.div key={kpi.key} variants={v.kpiItem} className='min-h-[118px]'>
          <OperacaoMetricCard
            label={kpi.label}
            value={kpi.value}
            numericValue={kpi.numericValue}
            valueFormat={kpi.valueFormat}
            subtitle={kpi.subtitle}
            trendPositive={kpi.trendPositive}
            icon={kpi.icon}
            accent={kpi.accent}
            valuesVisible={valuesVisible}
            animationDelay={(index + 1) * 90}
            variant='compact'
          />
        </motion.div>
      ))}
    </motion.div>
  )
}
